import { splitWithCertificate, type GiftCertificateView } from '@360face/domain';

/** What the payment screen shows once a certificate has been looked up. */
export type GiftPlan =
  | { kind: 'not_sold' }
  | { kind: 'used_up' }
  | { kind: 'covers_all'; coversMinor: number; leftMinor: number }
  | { kind: 'partial'; coversMinor: number; missingMinor: number };

export function giftPlan(billMinor: number, cert: GiftCertificateView): GiftPlan {
  if (cert.state === 'stock') return { kind: 'not_sold' };
  if (cert.balanceMinor <= 0) return { kind: 'used_up' };
  const s = splitWithCertificate(billMinor, cert.balanceMinor);
  return s.remainderMinor > 0
    ? { kind: 'partial', coversMinor: s.certificateMinor, missingMinor: s.remainderMinor }
    : { kind: 'covers_all', coversMinor: s.certificateMinor, leftMinor: s.balanceAfterMinor };
}

/** "0604821377" → "060 482 1377" while typing (printed numbers are 10 digits). */
export function groupCertificateCode(code: string): string {
  return /^\d{4,10}$/.test(code) ? [code.slice(0, 3), code.slice(3, 6), code.slice(6)].filter(Boolean).join(' ') : code;
}

/** Keeps only characters a certificate number may have (scanner input included). */
export function cleanCertificateCode(raw: string): string {
  return raw
    .replace(/[^A-Za-z0-9-]/g, '')
    .slice(0, 40)
    .toUpperCase();
}

/**
 * Euro amount typed by staff ("40", "40,5", "40.50") → integer cents, without floating point.
 * Returns undefined for anything else.
 */
export function parseEurosToMinor(raw: string): number | undefined {
  const m = /^(\d{1,5})(?:[.,](\d{1,2}))?$/.exec(raw.trim());
  if (!m) return undefined;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}
